import { z } from "zod";

import type { AuditChange } from "./organizations";
import {
	describeNewParty,
	describePartyChanges,
	endDateAfterSince,
	endDateNotBeforeSince,
	filledPartyText,
	missingPartyFields,
	optionalPartyText,
	PARTY_CONTACT_AUDIT_FIELDS,
	partyAddedAudit,
	partyContactFields,
	partyContactFrom,
	partyContactRecordFields,
	partyCurrencySchema,
	partyDateField,
	partyFieldsFrom,
	partyRecordFields,
	partyRemovedAudit,
	partyRequestFields,
	partyUpdatedAudit,
	type PartyAudit,
	type PartyField,
	type PartyRequirement,
} from "./parties";
import { PAYMENT_TERM_LABELS, paymentTermsSchema } from "./payment-terms";

// Customers: who the business sells to, and whether they are buying yet. What a customer shares with every other kind
// of party — its name, its dates, its note, whom to contact and how a change reads in the audit log — comes from
// `./parties.ts`; what is here is the commercial shape that makes it a customer, and the one field no other kind has:
// how far along the relationship is. The API and the frontend share these.

/**
 * How far along a customer is: somebody the business is talking to, or somebody buying from it. It is the word the
 * Directory shows while the relationship is on, so a live prospect reads "Prospect" and a live customer "Customer". A
 * customer who has churned is given an end date instead, and the Directory calls them "Ended" from the day after it —
 * which is why leaving is a date here and never a third stage that the dates could disagree with.
 */
export const CUSTOMER_STAGES = ["prospect", "customer"] as const;
export type CustomerStage = (typeof CUSTOMER_STAGES)[number];

export const CUSTOMER_STAGE_LABELS: Readonly<Record<CustomerStage, string>> = {
	prospect: "Prospect",
	customer: "Customer",
};

/** Whether the business sells to an organization or to a person. */
export const CUSTOMER_SEGMENTS = ["business", "individual"] as const;
export type CustomerSegment = (typeof CUSTOMER_SEGMENTS)[number];

export const CUSTOMER_SEGMENT_LABELS: Readonly<Record<CustomerSegment, string>> = {
	business: "Business",
	individual: "Individual",
};

/**
 * How far along a customer is, what it is, and on what terms it is sold to: the four choices a person's request cannot
 * do without, each one a value out of a fixed list, refused in the words the form shows while none is chosen.
 */
const customerChoiceFields = {
	stage: z.enum(CUSTOMER_STAGES, "Choose a stage"),
	segment: z.enum(CUSTOMER_SEGMENTS, "Choose a segment"),
	paymentTerms: paymentTermsSchema,
	/** What they are invoiced in, which need not be the company's own reporting currency. */
	currency: partyCurrencySchema,
};

/**
 * A customer as the customers table keeps it. The four choices are optional here and nowhere else: a person's request
 * always carries them, and only the Directory agent adds a customer without them (`CUSTOMER_REQUIREMENTS`).
 */
export const customerSchema = z.object({
	...partyRecordFields,
	...partyContactRecordFields,
	stage: customerChoiceFields.stage.optional(),
	segment: customerChoiceFields.segment.optional(),
	paymentTerms: customerChoiceFields.paymentTerms.optional(),
	currency: customerChoiceFields.currency.optional(),
	/** The entity on the contract, when it is not what the business calls them. */
	legalName: optionalPartyText(160).optional(),
	/** VAT or tax registration number, as it appears on their invoices. */
	taxId: optionalPartyText(40).optional(),
	/** Who in the business owns the relationship, in their own words — free text, never a reference to a person. */
	accountManager: optionalPartyText(120).optional(),
});
export type Customer = z.infer<typeof customerSchema>;

/**
 * The fields of a customer that a request carries, trimmed as the form sends them. The three optional text fields
 * accept "" because the form holds a field nobody filled in as an empty string, exactly as the contact block does. The
 * form spreads this rather than naming any of them again, so the two refuse the same thing in the same words and cap
 * the same lengths; only the fields an input owns — the dates it hands over as "" and the note the editor always
 * writes — are the form's own.
 */
export const customerRequestFields = {
	...partyRequestFields,
	...partyContactFields,
	...customerChoiceFields,
	legalName: optionalPartyText(160),
	taxId: optionalPartyText(40),
	accountManager: optionalPartyText(120),
};

const customerFieldsSchema = z.object(customerRequestFields);

/**
 * What the form sends to add or to change a customer. Add and change carry the same fields: unlike a person, there is
 * nothing the API owns that a client could send by mistake, and the note is written from the first save.
 */
export const customerRequestSchema = customerFieldsSchema.refine(endDateAfterSince, endDateNotBeforeSince);
export type CustomerRequest = z.infer<typeof customerRequestSchema>;

/**
 * The record an add or a change leaves behind: the id and the day it was added are the API's, and everything else is
 * what the request carried — a field somebody left empty left out rather than kept as "", and a note of nothing but
 * spaces no note at all. The two write handlers and the frontend's fake API all build a record this way, so none of
 * the three can drift from the others.
 */
export function customerFrom(id: string, addedAt: string, request: CustomerRequest): Customer {
	const { legalName, taxId, accountManager } = request;
	return {
		id,
		...partyFieldsFrom(request),
		...partyContactFrom(request),
		...filledPartyText({ legalName, taxId, accountManager }),
		stage: request.stage,
		segment: request.segment,
		paymentTerms: request.paymentTerms,
		currency: request.currency,
		addedAt,
	};
}

/**
 * What a person must fill in before a customer is complete, in form order and under the form's labels. A person's
 * request always carries all five; the Directory agent may add a customer with none of them, and the Directory shows
 * that customer as incomplete until somebody fills them in.
 */
export const CUSTOMER_REQUIREMENTS: readonly PartyRequirement<Customer>[] = [
	["stage", "Stage"],
	["segment", "Segment"],
	["paymentTerms", "Payment terms"],
	["currency", "Currency"],
	["since", "Start date"],
];

/** The labels of what a customer still lacks, in form order: none once somebody has filled it in. */
export function missingCustomerFields(customer: Customer): string[] {
	return missingPartyFields(customer, CUSTOMER_REQUIREMENTS);
}

/** The line under a customer's name: whether it is a business or a person, by label, or that nobody has said yet. */
export function customerDetail(customer: Customer): string {
	return customer.segment === undefined ? "No segment yet" : CUSTOMER_SEGMENT_LABELS[customer.segment];
}

/** How far along a customer is, by label, or that nobody has said yet: what its audit entries name it by. */
export function customerStageText(customer: Customer): string {
	return customer.stage === undefined ? "No stage yet" : CUSTOMER_STAGE_LABELS[customer.stage];
}

/**
 * Every field of a customer the audit log names, in the order an entry lists them — a stage, a segment and a payment
 * term by their label, never by the id the list keeps them under. The note is not among them: `describeNewParty` and
 * `describePartyChanges` report what became of it and never a word of what it says.
 */
const CUSTOMER_FIELDS: readonly PartyField<Customer>[] = [
	["Name", customer => customer.name],
	["Legal name", customer => customer.legalName],
	["Stage", customer => (customer.stage === undefined ? undefined : CUSTOMER_STAGE_LABELS[customer.stage])],
	["Segment", customer => (customer.segment === undefined ? undefined : CUSTOMER_SEGMENT_LABELS[customer.segment])],
	...PARTY_CONTACT_AUDIT_FIELDS,
	["Tax ID", customer => customer.taxId],
	[
		"Payment terms",
		customer => (customer.paymentTerms === undefined ? undefined : PAYMENT_TERM_LABELS[customer.paymentTerms]),
	],
	["Currency", customer => customer.currency],
	["Account manager", customer => customer.accountManager],
	partyDateField("Start date", customer => customer.since),
	partyDateField("End date", customer => customer.endDate),
];

/**
 * A new customer's values: every field it was added with, and nothing before it. An optional field nobody filled in
 * reads as `UNSPECIFIED_VALUE`, and a date nobody gave as `UNSPECIFIED_DATE`, the way `describeNewVendor` does.
 */
export function describeNewCustomer(customer: Customer): AuditChange[] {
	return describeNewParty(customer, CUSTOMER_FIELDS);
}

/** What changed between two versions of a customer, newest value first, for the audit log. */
export function describeCustomerChanges(before: Customer, after: Customer): AuditChange[] {
	return describePartyChanges(before, after, CUSTOMER_FIELDS);
}

/** The audit entry for a customer added to the Directory: "Added party" / "Helios Labs · Prospect". */
export function customerAddedAudit(customer: Customer): PartyAudit {
	return partyAddedAudit(customer.name, customerStageText(customer));
}

/** The audit entry for a customer whose record changed: "Updated party" / "Helios Labs · Stage: Customer". */
export function customerUpdatedAudit(customer: Customer, changes: readonly AuditChange[]): PartyAudit {
	return partyUpdatedAudit(customer.name, changes);
}

/** The audit entry for a customer taken out of the Directory: "Removed party" / "Helios Labs · Customer". */
export function customerRemovedAudit(customer: Customer): PartyAudit {
	return partyRemovedAudit(customer.name, customerStageText(customer));
}
